import type { OrderLeg } from '../services/orders.service';
import { formatBackendDate } from './dateUtils';

// Convert a date-ish value to YYYY-MM-DD, returning '' for empty input (vs.
// null) — matches the v1 API's "field present, blank" convention for snapshot
// rows.
// Wire fields the snapshot builders run through fmtDate. Kept next to them so an edit
// overlaid onto a removal row is formatted the same way the snapshot underneath it was
// — a raw Date where the rest of the row holds 'MM/DD/YYYY' would read as a change to a
// field the user never touched.
const DATE_ONLY_WIRE_FIELDS: ReadonlySet<string> = new Set([
  'origApptDate', 'destApptDate',
]);

const fmtDate = (d: unknown): string => {
  if (d === null || d === undefined) return '';
  return formatBackendDate(String(d)) || '';
};

// Per-leg snapshot row sent to the v1 save endpoint. The backend expects
// stringly-typed empty values for "no value" — we don't try to type the
// new/old payloads here.
export type LegSnapshotItem = {
  legrecno: number;
  legnum: number;
  legIndex: number;
  sendToGwiMove: boolean;
  new: Record<string, unknown>;
  old: Record<string, unknown>;
};

// Every leg field that ships in a save payload's `new`/`old` rows, in wire order.
// SOURCE OF TRUTH: orderLegDiff.ts partitions this list into the fields the pre-save
// stale check compares and the ones it deliberately ignores, and a type assertion there
// fails the build if a field lands in neither. A unit test asserts this list matches the
// keys actually emitted below, so adding a payload field forces both to be updated —
// the hand-maintained diff list is exactly how the destrequireinterchange stale-echo bug
// stayed invisible (see orderLegDiff.ts).
export const LEG_WIRE_FIELDS = [
  'legnum', 'legtype', 'legAction2', 'legDrop', 'legdeleted',
  'origcode', 'origName', 'origApptDate', 'origApptTime1', 'origApptTime2',
  'origApptNumber', 'origmemo', 'origArrivalDateTime', 'origDepartDateTime',
  'origgeoarrivaldatetime', 'origgeodepartdatetime', 'origapptrequesteddatetime',
  'origrequireinspection', 'origrequireinterchange', 'origrequirebol',
  'originspectionrecno', 'originterchangerecno', 'origbolrecno',
  'destcode', 'destName', 'destApptDate', 'destApptTime1', 'destApptTime2',
  'destApptNumber', 'legmemo', 'destArrivalDateTime', 'destDepartDateTime',
  'destgeoarrivaldatetime', 'destgeodepartdatetime', 'destapptrequesteddatetime',
  'destrequirepod', 'destrequireinterchange', 'destrequirescaleticket',
  'destpodrecno', 'destinterchangerecno', 'destscaleticketrecno',
  'driver', 'legstatus', 'dispatchid', 'donotpayleg', 'dspdriverdatetime',
] as const;

export type LegWireField = typeof LEG_WIRE_FIELDS[number];

export type LegSavePayload = {
  legs: LegSnapshotItem[];
  originateleg: number;
  deliveryleg: number;
  terminateleg: number;
};

type BuildParams = {
  originalLegs: OrderLeg[];     // from initial load — frozen reference for "old" values
  currentLegs: OrderLeg[];      // current visible legs (existing + new), excludes removes
  editedLegs: Record<number, Partial<OrderLeg>>;
  removedLegs: OrderLeg[];
};

// Build the leg snapshot for the v1 API save payload:
//   - One row per current visible leg (existing or pending-new)
//   - One row per removed leg (legdeleted: 'Y' on new, 'N' on old)
// Plus the order-level leg pointers computed from current visible legs with
// edits applied.
//
// "old" values come from originalLegs (the snapshot at last load), so the
// backend's stale-data check sees what the user was looking at when they
// edited. "new" values come from the current leg + pending edits.
export const buildLegSavePayload = ({
  originalLegs,
  currentLegs,
  editedLegs,
  removedLegs,
}: BuildParams): LegSavePayload => {
  // Existing legs use their position in originalLegs as legIndex; new legs
  // (legrecno < 0) get assigned slots after the last existing leg.
  let nextNewLegIndex = originalLegs.length + 1;

  const visibleRows: LegSnapshotItem[] = currentLegs.map(leg => {
    const isNew = leg.legrecno < 0;
    const originalLeg = isNew ? null : originalLegs.find(ol => ol.legrecno === leg.legrecno);
    const edits = editedLegs[leg.legrecno] || {};

    const legIndex = isNew
      ? nextNewLegIndex++
      : (originalLegs.findIndex(ol => ol.legrecno === leg.legrecno) + 1);

    const getValue = <K extends keyof OrderLeg>(field: K): OrderLeg[K] =>
      edits[field] !== undefined ? (edits[field] as OrderLeg[K]) : leg[field];

    // A driver change invalidates the existing dispatchid (it was tied to the
    // previous driver). Compare effective vs original so a change-and-revert
    // before save preserves the original dispatchid.
    const driverChanged =
      !isNew &&
      ((getValue('driver') as string | undefined) || '').trim() !==
        (originalLeg?.driver || '').trim();

    return {
      legrecno: leg.legrecno,
      legnum: leg.legnum,
      legIndex,
      sendToGwiMove: getValue('sendToGwiMove') || false,
      new: {
        legnum: leg.legnum,
        legtype: getValue('legtype') || '',
        legAction2: getValue('legAction2') || '',
        legDrop: getValue('legDrop') || 'N',
        legdeleted: 'N',
        origcode: getValue('origcode') || 0,
        origName: getValue('origName') || '',
        origApptDate: fmtDate(getValue('origApptDate')),
        origApptTime1: getValue('origApptTime1') || '',
        origApptTime2: getValue('origApptTime2') || '',
        origApptNumber: getValue('origApptNumber') || '',
        origmemo: getValue('origmemo') || '',
        origArrivalDateTime: getValue('origArrivalDateTime') || '',
        origDepartDateTime: getValue('origDepartDateTime') || '',
        origgeoarrivaldatetime: getValue('origgeoarrivaldatetime') || '',
        origgeodepartdatetime: getValue('origgeodepartdatetime') || '',
        origapptrequesteddatetime: getValue('origapptrequesteddatetime') || '',
        origrequireinspection: getValue('origrequireinspection') || 'N',
        origrequireinterchange: getValue('origrequireinterchange') || 'N',
        origrequirebol: getValue('origrequirebol') || 'N',
        originspectionrecno: getValue('originspectionrecno') || 0,
        originterchangerecno: getValue('originterchangerecno') || 0,
        origbolrecno: getValue('origbolrecno') || 0,
        destcode: getValue('destcode') || 0,
        destName: getValue('destName') || '',
        destApptDate: fmtDate(getValue('destApptDate')),
        destApptTime1: getValue('destApptTime1') || '',
        destApptTime2: getValue('destApptTime2') || '',
        destApptNumber: getValue('destApptNumber') || '',
        legmemo: getValue('legmemo') || '',
        destArrivalDateTime: getValue('destArrivalDateTime') || '',
        destDepartDateTime: getValue('destDepartDateTime') || '',
        destgeoarrivaldatetime: getValue('destgeoarrivaldatetime') || '',
        destgeodepartdatetime: getValue('destgeodepartdatetime') || '',
        destapptrequesteddatetime: getValue('destapptrequesteddatetime') || '',
        destrequirepod: getValue('destrequirepod') || 'N',
        destrequireinterchange: getValue('destrequireinterchange') || 'N',
        destrequirescaleticket: getValue('destrequirescaleticket') || 'N',
        destpodrecno: getValue('destpodrecno') || 0,
        destinterchangerecno: getValue('destinterchangerecno') || 0,
        destscaleticketrecno: getValue('destscaleticketrecno') || 0,
        driver: getValue('driver') || '',
        legstatus: getValue('legstatus') || '',
        dispatchid: driverChanged ? 0 : (getValue('dispatchid') || 0),
        donotpayleg: getValue('donotpayleg') || 'N',
        dspdriverdatetime: getValue('dspdriverdatetime') || '',
      },
      old: isNew ? {
        // New legs have no prior server state — all "old" values are blank.
        legnum: '', legtype: '', legAction2: '', legDrop: '', legdeleted: '',
        origcode: '', origName: '', origApptDate: '', origApptTime1: '', origApptTime2: '',
        origApptNumber: '', origmemo: '', origArrivalDateTime: '', origDepartDateTime: '',
        origgeoarrivaldatetime: '', origgeodepartdatetime: '', origapptrequesteddatetime: '',
        origrequireinspection: '', origrequireinterchange: '', origrequirebol: '',
        originspectionrecno: '', originterchangerecno: '', origbolrecno: '',
        destcode: '', destName: '', destApptDate: '', destApptTime1: '', destApptTime2: '',
        destApptNumber: '', legmemo: '', destArrivalDateTime: '', destDepartDateTime: '',
        destgeoarrivaldatetime: '', destgeodepartdatetime: '', destapptrequesteddatetime: '',
        destrequirepod: '', destrequireinterchange: '', destrequirescaleticket: '',
        destpodrecno: '', destinterchangerecno: '', destscaleticketrecno: '',
        driver: '', legstatus: '', dispatchid: '', donotpayleg: '', dspdriverdatetime: '',
      } : {
        legnum: originalLeg?.legnum ?? '',
        legtype: originalLeg?.legtype || '',
        legAction2: originalLeg?.legAction2 || '',
        legDrop: originalLeg?.legDrop || 'N',
        legdeleted: 'N',
        origcode: originalLeg?.origcode || 0,
        origName: originalLeg?.origName || '',
        origApptDate: fmtDate(originalLeg?.origApptDate),
        origApptTime1: originalLeg?.origApptTime1 || '',
        origApptTime2: originalLeg?.origApptTime2 || '',
        origApptNumber: originalLeg?.origApptNumber || '',
        origmemo: originalLeg?.origmemo || '',
        origArrivalDateTime: originalLeg?.origArrivalDateTime || '',
        origDepartDateTime: originalLeg?.origDepartDateTime || '',
        origgeoarrivaldatetime: originalLeg?.origgeoarrivaldatetime || '',
        origgeodepartdatetime: originalLeg?.origgeodepartdatetime || '',
        origapptrequesteddatetime: originalLeg?.origapptrequesteddatetime || '',
        origrequireinspection: originalLeg?.origrequireinspection || 'N',
        origrequireinterchange: originalLeg?.origrequireinterchange || 'N',
        origrequirebol: originalLeg?.origrequirebol || 'N',
        originspectionrecno: originalLeg?.originspectionrecno || 0,
        originterchangerecno: originalLeg?.originterchangerecno || 0,
        origbolrecno: originalLeg?.origbolrecno || 0,
        destcode: originalLeg?.destcode || 0,
        destName: originalLeg?.destName || '',
        destApptDate: fmtDate(originalLeg?.destApptDate),
        destApptTime1: originalLeg?.destApptTime1 || '',
        destApptTime2: originalLeg?.destApptTime2 || '',
        destApptNumber: originalLeg?.destApptNumber || '',
        legmemo: originalLeg?.legmemo || '',
        destArrivalDateTime: originalLeg?.destArrivalDateTime || '',
        destDepartDateTime: originalLeg?.destDepartDateTime || '',
        destgeoarrivaldatetime: originalLeg?.destgeoarrivaldatetime || '',
        destgeodepartdatetime: originalLeg?.destgeodepartdatetime || '',
        destapptrequesteddatetime: originalLeg?.destapptrequesteddatetime || '',
        destrequirepod: originalLeg?.destrequirepod || 'N',
        destrequireinterchange: originalLeg?.destrequireinterchange || 'N',
        destrequirescaleticket: originalLeg?.destrequirescaleticket || 'N',
        destpodrecno: originalLeg?.destpodrecno || 0,
        destinterchangerecno: originalLeg?.destinterchangerecno || 0,
        destscaleticketrecno: originalLeg?.destscaleticketrecno || 0,
        driver: originalLeg?.driver || '',
        legstatus: originalLeg?.legstatus || '',
        dispatchid: originalLeg?.dispatchid || 0,
        donotpayleg: originalLeg?.donotpayleg || 'N',
        dspdriverdatetime: originalLeg?.dspdriverdatetime || '',
      },
    };
  });

  const removedRows: LegSnapshotItem[] = removedLegs.map(leg => {
    const legFields = {
      legnum: leg.legnum,
      legtype: leg.legtype || '',
      legAction2: leg.legAction2 || '',
      legDrop: leg.legDrop || 'N',
      origcode: leg.origcode || 0,
      origName: leg.origName || '',
      origApptDate: fmtDate(leg.origApptDate),
      origApptTime1: leg.origApptTime1 || '',
      origApptTime2: leg.origApptTime2 || '',
      origApptNumber: leg.origApptNumber || '',
      origmemo: leg.origmemo || '',
      origArrivalDateTime: leg.origArrivalDateTime || '',
      origDepartDateTime: leg.origDepartDateTime || '',
      origgeoarrivaldatetime: leg.origgeoarrivaldatetime || '',
      origgeodepartdatetime: leg.origgeodepartdatetime || '',
      origapptrequesteddatetime: leg.origapptrequesteddatetime || '',
      origrequireinspection: leg.origrequireinspection || 'N',
      origrequireinterchange: leg.origrequireinterchange || 'N',
      origrequirebol: leg.origrequirebol || 'N',
      originspectionrecno: leg.originspectionrecno || 0,
      originterchangerecno: leg.originterchangerecno || 0,
      origbolrecno: leg.origbolrecno || 0,
      destcode: leg.destcode || 0,
      destName: leg.destName || '',
      destApptDate: fmtDate(leg.destApptDate),
      destApptTime1: leg.destApptTime1 || '',
      destApptTime2: leg.destApptTime2 || '',
      destApptNumber: leg.destApptNumber || '',
      legmemo: leg.legmemo || '',
      destArrivalDateTime: leg.destArrivalDateTime || '',
      destDepartDateTime: leg.destDepartDateTime || '',
      destgeoarrivaldatetime: leg.destgeoarrivaldatetime || '',
      destgeodepartdatetime: leg.destgeodepartdatetime || '',
      destapptrequesteddatetime: leg.destapptrequesteddatetime || '',
      destrequirepod: leg.destrequirepod || 'N',
      destrequireinterchange: leg.destrequireinterchange || 'N',
      destrequirescaleticket: leg.destrequirescaleticket || 'N',
      destpodrecno: leg.destpodrecno || 0,
      destinterchangerecno: leg.destinterchangerecno || 0,
      destscaleticketrecno: leg.destscaleticketrecno || 0,
      driver: leg.driver || '',
      legstatus: leg.legstatus || '',
      dispatchid: leg.dispatchid || 0,
      donotpayleg: leg.donotpayleg || 'N',
      dspdriverdatetime: leg.dspdriverdatetime || '',
    };
    const legIndex = originalLegs.findIndex(ol => ol.legrecno === leg.legrecno) + 1;

    // Edits the user made to this leg BEFORE removing it have to travel with the
    // removal. The common one is clearing the arrival/depart times: removeLeg won't
    // let an arrived stop go until they're blank, so "clear, then delete" is the
    // supported way to do it — and if the clear never reaches the payload, the save
    // writes only legdeleted and the timestamps stay on the row (pro 5104104 leg
    // 12047270 is exactly that: deleted, with driver 5881 and all four datetimes
    // still on it).
    //
    // `old` deliberately stays the SERVER snapshot: `removedLegs` comes from
    // orderDetails.legs, so it already matches the DB, and the conflict gate compares
    // that `old` against the current row to catch anyone else moving the leg
    // underneath. Overlaying edits onto `new` only is what keeps both true at once —
    // the diff clears the values AND removes the stop, in one save.
    const edits = editedLegs[leg.legrecno] || {};
    const editedWire: Record<string, unknown> = {};
    (Object.keys(edits) as Array<keyof OrderLeg>).forEach(field => {
      if (!(field in legFields) || edits[field] === undefined) return;
      editedWire[field as string] = DATE_ONLY_WIRE_FIELDS.has(field as string)
        ? fmtDate(edits[field] as OrderLeg[keyof OrderLeg])
        : edits[field];
    });

    return {
      legrecno: leg.legrecno,
      legnum: leg.legnum,
      legIndex,
      sendToGwiMove: false,
      new: { ...legFields, ...editedWire, legdeleted: 'Y' },
      old: { ...legFields, legdeleted: 'N' },
    };
  });

  // Order-level leg pointers — find the visible leg whose effective legtype/
  // legAction2 matches each role.
  const effective = (l: OrderLeg, field: 'legtype' | 'legAction2'): string =>
    ((editedLegs[l.legrecno]?.[field] as string | undefined) ?? l[field] ?? '');

  const originateleg = currentLegs.find(l => effective(l, 'legtype') === 'PICKUP')?.legnum ?? 0;
  const deliveryleg = currentLegs.find(l => effective(l, 'legAction2') === 'DELIVERY')?.legnum ?? 0;
  const terminateleg = currentLegs.find(l => effective(l, 'legAction2') === 'TERMINATE')?.legnum ?? 0;

  return {
    legs: [...visibleRows, ...removedRows],
    originateleg,
    deliveryleg,
    terminateleg,
  };
};
